import { Button, Dropdown, MenuProps, Modal, Space, Spin, Table, Typography, Upload } from 'antd';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import ILoan from '~Api/Loan/ILoan';
import LoanStatusEnum from '~Api/Loan/LoanStatusEnum';
import { IGlobalState } from '~reducer';
import { useParams } from 'react-router-dom';
import {
    loanDrawdownConditionDeleteAction,
    loanDrawdownConditionDocumentDeleteAction,
    loanDrawdownConditionDocumentsAddAction,
    loanDrawdownConditionsWithDocumentsListAction,
} from '~LoanDrawdowns/actions';
import {
    loanDrawdownConditionsWithDocumentsSelector,
} from '~LoanDrawdowns/selectors';
import Layout from './Layout';
import { IDictionary } from '~utilities/IDictionary';
import _ from 'lodash';
import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/lib/table';
import ILoanDrawdownCondition from '~Api/LoanDrawdowns/ILoanDrawdownCondition';
import {
    loanDrawdownSelector,
    loanSelector,
} from '~Loans/selectors';
import {
    loanDrawdownGetAction,
} from '~Loans/actions';
import ILoanDrawdownConditionDocument from '~Api/LoanDrawdowns/ILoanDrawdownConditionDocument';
import { BsCloud } from 'react-icons/bs';
import ConditionAddModal from './ConditionAddModal';
import ModalStateEnum from '~UI/ModalStateEnum';

export default function Conditions(): ReactElement {
    const { loanDrawdownUuid, loanUuid } = useParams<{ loanUuid: string; loanDrawdownUuid: string }>();

    const [ addConditionModalOpen, setAddConditionModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const loanDrawdown: ILoanDrawdown = useSelector((state: IGlobalState) => loanDrawdownSelector(state, loanDrawdownUuid));
    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, loanUuid));
    const loanDrawdownConditions: IDictionary<ILoanDrawdownCondition> = useSelector((state: IGlobalState) => loanDrawdownConditionsWithDocumentsSelector(state, loanDrawdownUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!loanDrawdownConditions) {
            dispatch(loanDrawdownConditionsWithDocumentsListAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        loanDrawdownConditions,
        loanDrawdownUuid,
    ]);

    useEffect(() => {
        if (!loanDrawdown) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        loanDrawdown,
        loanDrawdownUuid,
    ]);

    const onClickAddDrawdownCondition: () => void = useCallback(() => {
        setAddConditionModalOpen(ModalStateEnum.Open);
    }, []);

    const onCancelDrawdownConditionModal: () => void = useCallback(() => {
        setAddConditionModalOpen(ModalStateEnum.Closed);
    }, []);

    if (!loanDrawdown || !loan) {
        return (
            <Layout loanUuid={loanUuid} section='conditions' loanDrawdownUuid={loanDrawdownUuid}>
                <Typography.Title level={2}>Conditions</Typography.Title>
                <Spin/>
            </Layout>
        );
    }

    const addConditionModalBlock: ReactElement = ModalStateEnum.Off !== addConditionModalOpen && (
        <ConditionAddModal
            loanDrawdown={loanDrawdown}
            isOpen={addConditionModalOpen === ModalStateEnum.Open}
            onCancel={onCancelDrawdownConditionModal}
        />
    );

    const columns: ColumnsType<ILoanDrawdownCondition> = [
        {
            dataIndex: 'name',
            title: 'Condition',
        },
        {
            render: (loanDrawdownCondition: ILoanDrawdownCondition): ReactElement[] => {
                return _.map(loanDrawdownCondition.documents, (loanDrawdownConditionDocument: ILoanDrawdownConditionDocument) => {
                    const documentButton: ReactElement = (
                        <Button
                            className='document'
                            href={`${process.env.API_HOST}/loan-drawdown-condition-documents/${loanDrawdownConditionDocument.uuid}/download`}
                            key={loanDrawdownConditionDocument.uuid}
                            target='_blank'
                            type='link'
                        >
                            <BsCloud size={16} />
                        </Button>
                    );

                    if (LoanStatusEnum.ClosedObligationsMet === loan.status) {
                        return documentButton;
                    }

                    const menu: MenuProps = {
                        className: 'uploaded-condition-document-dropdown',
                        items: [
                            {
                                children: [
                                    {
                                        danger: true,
                                        icon: <DeleteOutlined/>,
                                        key: `${loanDrawdownConditionDocument.uuid}-delete`,
                                        label: 'Delete',
                                        onClick: (): void => {
                                            Modal.confirm({
                                                content: `Are you sure you want to delete this ${loanDrawdownCondition.name} document?`,
                                                okText: 'Delete',
                                                okType: 'danger',
                                                onOk: () => {
                                                    dispatch(loanDrawdownConditionDocumentDeleteAction(loanDrawdownConditionDocument.uuid));
                                                },
                                                title: 'Delete Document',
                                            });
                                        },
                                    },
                                ],
                                label: loanDrawdownConditionDocument.fileName,
                                type: 'group',
                            },
                        ],
                    };

                    return (
                        <Dropdown key={loanDrawdownConditionDocument.uuid} menu={menu}>
                            {documentButton}
                        </Dropdown>
                    );
                });
            },
            title: 'Documents',
            width: 250,
        },
        ...(LoanStatusEnum.ClosedObligationsMet !== loan.status ? [
            {
                render: (loanDrawdownCondition: ILoanDrawdownCondition): ReactElement => {
                    const onClickDeleteCondition: () => void = (): void => {
                        Modal.confirm({
                            content: 'Are you sure you want to delete this condition?',
                            okText: 'Delete',
                            okType: 'danger',
                            onOk: () => {
                                dispatch(loanDrawdownConditionDeleteAction(loanDrawdownCondition.uuid));
                            },
                            title: 'Delete Condition',
                        });
                    };

                    const beforeUpload: (file: File) => boolean = (file: File): boolean => {
                        dispatch(loanDrawdownConditionDocumentsAddAction(file, loanDrawdownCondition.uuid));

                        return false;
                    };

                    return (
                        <Space>
                            <Upload className='add-document' beforeUpload={beforeUpload} showUploadList={false}>
                                <Button type='link'><UploadOutlined/></Button>
                            </Upload>
                            <Button
                                danger={true}
                                type='link'
                                onClick={onClickDeleteCondition}
                            >
                                <DeleteOutlined/>
                            </Button>
                        </Space>
                    );
                },
                title: 'Actions',
                width: 150,
            },
        ] : []),
    ];

    return (
        <Layout loanUuid={loanUuid} section='conditions' loanDrawdownUuid={loanDrawdownUuid}>
            <Space className='actions'>
                {LoanStatusEnum.ClosedObligationsMet !== loan.status && <Button onClick={onClickAddDrawdownCondition}>Add Condition</Button>}
            </Space>
            <Typography.Title level={2}>Conditions</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(loanDrawdownConditions)}
                pagination={false}
                rowKey='uuid'
                size='middle'
                loading={!loanDrawdownConditions}
            />
            {addConditionModalBlock}
        </Layout>
    );
}